import { cn } from '@/lib/utils'
import type { PanelArtProps } from './panel-art-props'

/**
 * Flow Graph panel art: a sankey of three node columns, two then three then
 * two, joined by ribbons whose thickness carries the share of days taking each
 * route.
 *
 * Ribbons are filled closed paths rather than fat strokes, so the thickness at
 * each end is exact and the shape needs no id for a gradient or a mask.
 */

/** [x, top y, height] for each node. */
const NODES: [number, number, number][] = [
  [12, 18, 30],
  [12, 54, 28],
  [75, 14, 24],
  [75, 44, 12],
  [75, 68, 18],
  [138, 16, 28],
  [138, 52, 26],
]

/**
 * [left x, left top, left bottom, right x, right top, right bottom]. Ribbons
 * leaving one node are separated by a two unit gap so they stay legible where
 * they bunch together.
 */
const RIBBONS: [number, number, number, number, number, number][] = [
  [22, 18, 34, 75, 14, 30],
  [22, 36, 48, 75, 44, 56],
  [22, 54, 62, 75, 30, 38],
  [22, 64, 82, 75, 68, 86],
  [85, 14, 30, 138, 16, 32],
  [85, 30, 38, 138, 52, 60],
  [85, 44, 56, 138, 32, 44],
  [85, 68, 86, 138, 60, 78],
]

/** Two cubics joined by vertical ends, so each side keeps its exact thickness. */
function ribbonPath(
  x0: number,
  top0: number,
  bottom0: number,
  x1: number,
  top1: number,
  bottom1: number,
): string {
  const mid = (x0 + x1) / 2
  return [
    `M${x0} ${top0}`,
    `C${mid} ${top0}, ${mid} ${top1}, ${x1} ${top1}`,
    `L${x1} ${bottom1}`,
    `C${mid} ${bottom1}, ${mid} ${bottom0}, ${x0} ${bottom0}`,
    'Z',
  ].join(' ')
}

export function FlowGraphArt({ className }: PanelArtProps) {
  return (
    <svg
      viewBox="0 0 160 100"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
      className={cn('h-full w-full', className)}
    >
      {RIBBONS.map((ribbon) => (
        <path
          key={ribbon.join('-')}
          d={ribbonPath(...ribbon)}
          fill="hsl(var(--primary) / 0.35)"
          stroke="none"
        />
      ))}
      {NODES.map(([x, y, height]) => (
        <rect
          key={`${x}-${y}`}
          x={x}
          y={y}
          width={10}
          height={height}
          rx={2}
          fill="hsl(var(--foreground) / 0.38)"
        />
      ))}
    </svg>
  )
}
